import React, { useState, useEffect } from "react";
import api from "../../utils/Api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export default function ListadoHorasT() {
  const [state, setState] = useState({
    empleados: [],
    filtroEmpleado: "",
    fechaInicio: "",
    fechaFin: "",
    listadoHoras: [],
    loading: false,
    error: null,
    paginaActual: 1,
    registrosPorPagina: 10,
  });

  // ================= UTILIDADES =================

  const hmsASegundos = (hms) => {
    const [h, m, s] = hms.split(":").map(Number);
    return h * 3600 + m * 60 + s;
  };

  const segundosAHMS = (segundos) => {
    const h = Math.floor(segundos / 3600);
    const m = Math.floor((segundos % 3600) / 60);
    const s = segundos % 60;
    return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
  };

  const formatearFecha = (fecha) => {
    const [fechaParte] = fecha.split(" ");
    const [año, mes, dia] = fechaParte.split("-");
    return `${dia}/${mes}/${año}`;
  };

  const formatearFechaHora = (fecha) => {
    const [, horaParte] = fecha.split(" ");
    return horaParte;
  };

  // ================= API =================

  const fetchEmpleados = async () => {
    try {
      const response = await api.get("/usuarios");
      setState((prev) => ({ ...prev, empleados: response.data }));
    } catch {
      setState((prev) => ({ ...prev, error: "Error cargando empleados" }));
    }
  };

  const fetchListadoHoras = async (filtros = {}) => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await api.post("/fichajes/listado", filtros);
      const ordenado = [...response.data.data].sort((a, b) => a.id - b.id);
      setState((prev) => ({
        ...prev,
        listadoHoras: ordenado,
        paginaActual: 1, // 🔹 reset paginación
        error: null,
      }));
    } catch {
      setState((prev) => ({
        ...prev,
        error: "Error cargando listado de horas",
      }));
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchEmpleados();
  }, []);

  // ================= PDF =================

  const generarPDF = async () => {
    const { filtroEmpleado, fechaInicio, fechaFin, empleados } = state;

    if (!filtroEmpleado) {
      setState((prev) => ({ ...prev, error: "Seleccione un empleado" }));
      return;
    }

    try {
      const response = await api.post("/fichajes/listado", {
        empleado: filtroEmpleado,
        fechaInicio,
        fechaFin,
      });

      const registros = response.data.data;

      const totalSegundos = registros
        .filter((r) => r.duracion)
        .reduce((acc, r) => acc + hmsASegundos(r.duracion), 0);

      const totalHoras = segundosAHMS(totalSegundos);

      const empleado = empleados.find(
        (e) => e.id === Number(filtroEmpleado)
      );

      const doc = new jsPDF();

      doc.setFontSize(18);
      doc.setTextColor(254, 127, 37);
      doc.text("Listado de Horas Trabajadas", 14, 20);

      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text(`Empleado: ${empleado?.nombre}`, 14, 30);
      doc.text(`Periodo: ${fechaInicio || "-"} / ${fechaFin || "-"}`, 14, 35);
      doc.text(`Total horas: ${totalHoras}`, 140, 30);

      autoTable(doc, {
        startY: 45,
        head: [["Fecha", "Tipo", "Hora", "Duración"]],
        body: registros.map((r) => [
          formatearFecha(r.fecha_hora),
          r.tipo,
          formatearFechaHora(r.fecha_hora),
          r.duracion ?? "-",
        ]),
        theme: "grid",
        headStyles: { fillColor: [254, 127, 37] },
      });

      doc.save(`horas_${empleado?.nombre}.pdf`);
    } catch {
      setState((prev) => ({ ...prev, error: "Error generando PDF" }));
    }
  };

  // ================= FORM =================

  const handleSubmit = (e) => {
    e.preventDefault();
    const { filtroEmpleado, fechaInicio, fechaFin } = state;

    if (!filtroEmpleado) {
      setState((prev) => ({ ...prev, error: "Seleccione un empleado" }));
      return;
    }

    if (fechaInicio && fechaFin && fechaInicio > fechaFin) {
      setState((prev) => ({
        ...prev,
        error: "La fecha inicio no puede ser mayor",
      }));
      return;
    }

    fetchListadoHoras({ empleado: filtroEmpleado, fechaInicio, fechaFin });
  };

  // ================= PAGINACIÓN =================

  const { paginaActual, registrosPorPagina, listadoHoras } = state;
  const indiceUltimo = paginaActual * registrosPorPagina;
  const indicePrimero = indiceUltimo - registrosPorPagina;

  const registrosPaginados = listadoHoras.slice(
    indicePrimero,
    indiceUltimo
  );

  const totalPaginas = Math.ceil(listadoHoras.length / registrosPorPagina);

  // ================= TOTALES =================

  const totalSegundosUI = listadoHoras
    .filter((r) => r.duracion)
    .reduce((acc, r) => acc + hmsASegundos(r.duracion), 0);

  const totalHorasUI = segundosAHMS(totalSegundosUI);

  // ================= RENDER =================

  return (
    <div className="container-fluid">
      <h1 className="fs-3 fw-bold">Listado de horas trabajadas</h1>
      <form onSubmit={handleSubmit} className="bg-light p-3 mb-4 rounded">
        <div className="row g-3">
          <div className="col-md-4">
            <label>Empleado</label>
            <select
              className="form-select"
              value={state.filtroEmpleado}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  filtroEmpleado: e.target.value,
                }))
              }
            >
              <option value="">Seleccione</option>
              {state.empleados.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label>Fecha inicio</label>
            <input
              type="date"
              className="form-control"
              value={state.fechaInicio}
              onChange={(e) =>
                setState((prev) => ({ ...prev, fechaInicio: e.target.value }))
              }
            />
          </div>

          <div className="col-md-4">
            <label>Fecha fin</label>
            <input
              type="date"
              className="form-control"
              value={state.fechaFin}
              onChange={(e) =>
                setState((prev) => ({ ...prev, fechaFin: e.target.value }))
              }
            />
          </div>

          <div className="col-12 d-flex justify-content-between">
            <button className="btn btn-primary">Filtrar</button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={generarPDF}
              disabled={!listadoHoras.length}
            >
              Exportar PDF
            </button>
          </div>
        </div>
      </form>

      {state.error && <p className="text-danger">{state.error}</p>}

      <p>
        <strong>Total registros:</strong> {listadoHoras.length} |{" "}
        <strong>Total horas:</strong> {totalHorasUI}
      </p>

      <table className="table table-striped">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Tipo</th>
            <th>Hora</th>
            <th>Duración</th>
          </tr>
        </thead>
        <tbody>
          {registrosPaginados.length === 0 ? (
            <tr>
              <td colSpan="4" className="text-center">
                No hay registros
              </td>
            </tr>
          ) : (
            registrosPaginados.map((r) => (
              <tr key={r.id}>
                <td>{formatearFecha(r.fecha_hora)}</td>
                <td>{r.tipo}</td>
                <td>{formatearFechaHora(r.fecha_hora)}</td>
                <td>{r.duracion ?? "-"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

    {totalPaginas > 1 && (
      <nav>
        <ul
          style={{
            listStyle: "none",
            display: "flex",
            gap: 8,
            justifyContent: "center",
            marginTop: 10
          }}
        >
          <li>
            <button
              disabled={paginaActual === 1}
              onClick={() =>
                setState(prev => ({
                  ...prev,
                  paginaActual: Math.max(prev.paginaActual - 1, 1)
                }))
              }
            >
              Anterior
            </button>
          </li>

          {[...Array(totalPaginas)].map((_, i) => (
            <li key={i}>
              <button
                onClick={() =>
                  setState(prev => ({ ...prev, paginaActual: i + 1 }))
                }
                style={{
                  fontWeight: paginaActual === i + 1 ? "bold" : "normal",
                  textDecoration:
                    paginaActual === i + 1 ? "underline" : "none"
                }}
              >
                {i + 1}
              </button>
            </li>
          ))}

          <li>
            <button
              disabled={paginaActual === totalPaginas}
              onClick={() =>
                setState(prev => ({
                  ...prev,
                  paginaActual: Math.min(prev.paginaActual + 1, totalPaginas)
                }))
              }
            >
              Siguiente
            </button>
          </li>
        </ul>
      </nav>
    )}
  </div>
  );
}
